// File: SkinExpert/src/app/layout/cases/CaseDetailsView.tsx

import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Fade,
  useTheme,
  alpha,
  useMediaQuery,
  Stack,
  Divider,
  Button,
  IconButton,
  Tooltip,
  Alert,
  Paper,
} from "@mui/material";
import FinalizedReportsLinks from './components/FinalizedReportsLinks';
import TourGuide from '../../../features/components/TourGuide';

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";
import BiotechIcon from "@mui/icons-material/Biotech";
import CategoryIcon from "@mui/icons-material/Category";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScienceIcon from "@mui/icons-material/Science";
import DescriptionIcon from "@mui/icons-material/Description";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloseIcon from "@mui/icons-material/Close";
import SchoolIcon from "@mui/icons-material/School";
import ReactMarkdown from "react-markdown";
import AuthService from "../../services/AuthService";
import KnowledgeGraph from "../chatbot/KnowledgeGraph";
import {
  getCaseBlocks,
  getCaseExtractions, // Add this
  ExtractedEntity,
  StructuredExtraction,
  extractEntities,
  extractStructured,
} from "../../services/caseApi";
import {
  listCaseVersions,
  activateVersion,
  getActiveVersion,
  getReportById,
  VersionOut,
} from "../../services/reportAPI";



const API_BASE_URL = process.env.REACT_APP_API_URL;

interface LLMInteraction {
  id: string;
  data: {
    prompt: string;
    response: string;
    stage: string;
    createdAt: string;
    model?: string;
    confidence?: number;
  };
}

interface SegmentationResult {
  id: string;
  data: {
    segmentedPlot: string;
    assymetryPlot1: string;
    assymetryplot2: string;
    colorplot: string;
    profileText: string;
    circularity: number;
    asymmetry: number;
    createdAt: string;
  };
}

interface CaseData {
  caseId: string;
  fileName: string;
  filePath: string;
  patientId?: string;
  createdAt: string;
  mimeType: string;
}

export default function CaseDetailsView() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isDarkMode = theme.palette.mode === "dark";

  const patientId = searchParams.get("patientId");

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [caseData, setCaseData] = useState<CaseData | null>(null);
  const [llmInteractions, setLlmInteractions] = useState<LLMInteraction[]>([]);
  const [segmentationResults, setSegmentationResults] = useState<SegmentationResult[]>([]);
  const [extractedEntities, setExtractedEntities] = useState<ExtractedEntity[]>([]);
  const [structuredExtraction, setStructuredExtraction] = useState<StructuredExtraction | null>(null);
  
  // Version control state
  const [versions, setVersions] = useState<VersionOut[]>([]);
  const [activeVersion, setActiveVersion] = useState<VersionOut | null>(null);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [selectedReportContent, setSelectedReportContent] = useState<string | null>(null);
  const [activeReportContent, setActiveReportContent] = useState<string | null>(null);
  
  // Loading states for extractions
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [loadingStructured, setLoadingStructured] = useState(false);
  
  // Tour state
  const [runTour, setRunTour] = useState(false);
  
  // Alert state for extraction validation
  const [showExtractionAlert, setShowExtractionAlert] = useState(false);
  
  const [expandedAccordions, setExpandedAccordions] = useState<{
    [key: string]: boolean;
  }>({
    llm: true,
    segmentation: false,
    entities: false,
    structured: false,
    knowledgeGraph: false,
  });

  const authService = new AuthService();
  const baseUrl = process.env.REACT_APP_API_URL_;

  // Utility function to remove embedded images from markdown
  const removeAttachedImagesSection = (markdown: string | null): string | null => {
    if (!markdown) return null;
    
    // Remove all markdown image syntax: ![alt text](url)
    // This regex matches ![anything](anything.png) or .jpg, .jpeg, .gif, etc.
    const imagePattern = /!\[.*?\]\(.*?\.(png|jpg|jpeg|gif|webp|svg).*?\)/gi;
    
    const cleaned = markdown.replace(imagePattern, '').trim();
    
    console.log("🔍 Original markdown length:", markdown.length);
    console.log("🔍 Cleaned markdown length:", cleaned.length);
    console.log("🔍 Removed", markdown.length - cleaned.length, "characters");
    
    return cleaned;
  };

  // Tour steps - conditional based on whether versions exist
  const tourSteps = React.useMemo(() => {
    if (!activeVersion) {
      // No versions exist yet - show simplified tour
      return [
        {
          target: '[data-tour="version-control"]',
          content: '👋 Welcome to Case Details! This is where you\'ll manage case reports and versions. Currently, no report versions exist for this case.',
          disableBeacon: true,
        },
        {
          target: 'button[data-tour="create-report"]',
          content: '📝 Start by clicking "Create Report" to generate your first analysis. After creating a report, re-run this tour to explore advanced features like version control, knowledge graphs, and taxonomy extraction!',
          disableBeacon: true,
        },
      ];
    }

    // Full tour when versions exist
    return [
      {
        target: '[data-tour="version-control"]',
        content: 'Version Control tracks all iterations of your case reports. v0 is the initial AI analysis, and each finalized report creates a new version (v1, v2, etc.). Only one version is active at a time.',
        disableBeacon: true,
      },
      {
        target: '[data-tour="active-version"]',
        content: 'This shows which version is currently active. Click on other versions to switch between different report iterations and see how the analysis evolved.',
        disableBeacon: true,
      },
      {
        target: '[data-tour="generate-kg"]',
        content: 'Generate Knowledge Graph extracts medical entities, diagnoses, and relationships from the active report version. This powers the taxonomy filters and creates structured data.',
        disableBeacon: true,
      },
      {
        target: '[data-tour="new-report"]',
        content: 'Create a new report version based on the current active version. This allows you to refine and improve the analysis while keeping a history of all changes.',
        disableBeacon: true,
      },
      {
        target: '[data-tour="report-content"]',
        content: 'This section shows the report content. For finalized reports (v1+), you\'ll see the markdown report. For v0, you\'ll see the original AI analysis interactions.',
        disableBeacon: true,
      },
      {
        target: '[data-tour="segmentation"]',
        content: 'Segmentation Analysis provides detailed metrics about the lesion including circularity, asymmetry, and color distribution with visual plots.',
        disableBeacon: true,
      },
      {
        target: '[data-tour="entities"]',
        content: 'Medical Entities are automatically extracted using Named Entity Recognition (NER) to identify key clinical terms and concepts from the report.',
        disableBeacon: true,
      },
      {
        target: '[data-tour="structured"]',
        content: 'Structured Clinical Assessment organizes the diagnosis, differential diagnoses, malignancy risk, location, characteristics, and recommendations in a standardized format.',
        disableBeacon: true,
      },
      {
        target: '[data-tour="knowledge-graph"]',
        content: 'Knowledge Graph visualizes the relationships between diagnoses, symptoms, locations, and other medical entities extracted from the report in an interactive network diagram.',
        disableBeacon: true,
      },
    ];
  }, [activeVersion]);

  const handleStartTour = (e: React.MouseEvent) => {
    e.preventDefault();
    localStorage.removeItem("tour_case-details_completed");
    localStorage.removeItem("tour_case-details_step");
    setRunTour(false);
    setTimeout(() => setRunTour(true), 50);
  };

  const handleFinishTour = () => {
    setRunTour(false);
  };

  useEffect(() => {
    if (caseId) {
      // Fetch versions first to know which version is active
      fetchVersions().then((activeVer) => {
        // Then fetch case data with the active version number
        if (activeVer) {
          fetchCaseData(activeVer.versionNumber);
        } else {
          fetchCaseData();
        }
      });
    }
  }, [caseId]);

  const fetchVersions = async () => {
    if (!caseId) return null;
    
    setLoadingVersions(true);
    try {
      const accessToken = await authService.getAccessToken();
      if (!accessToken) throw new Error("No access token available");
      
      // Fetch all versions and active version in parallel
      const [versionsData, activeVersionData] = await Promise.all([
        listCaseVersions(accessToken, caseId),
        getActiveVersion(accessToken, caseId).catch(() => null),
      ]);
      
      setVersions(versionsData);
      setActiveVersion(activeVersionData);
      
      console.log("✅ Loaded versions:", versionsData);
      console.log("✅ Active version:", activeVersionData);
      
      // If active version has a report, fetch the report content
      if (activeVersionData?.reportId) {
        try {
          const reportData = await getReportById(accessToken, activeVersionData.reportId);
          setActiveReportContent(reportData.markdownContent);
          console.log("✅ Loaded active report content");
        } catch (err: any) {
          console.error("❌ Error fetching active report (may have been deleted):", err);
          // Report was deleted but version still references it - clear the content
          setActiveReportContent(null);
          // Don't auto-refresh to avoid infinite loop - user can manually refresh
        }
      } else {
        setActiveReportContent(null);
      }
      
      return activeVersionData;
    } catch (err: any) {
      console.error("❌ Error fetching versions:", err);
      return null;
    } finally {
      setLoadingVersions(false);
    }
  };

  const fetchCaseData = async (versionNumber?: number) => {
    if (!caseId) return;

    setLoading(true);
    setError(null);

    try {
      const accessToken = await authService.getAccessToken();
      if (!accessToken) {
        throw new Error("No access token available");
      }
      
      // Fetch case blocks AND stored extractions in parallel
      console.log("📥 Fetching case data for caseId:", caseId, "version:", versionNumber);
      
      const [caseResponse, extractionsResponse] = await Promise.all([
        getCaseBlocks(accessToken, caseId),
        getCaseExtractions(accessToken, caseId, versionNumber),
      ]);
      
      console.log("✅ Case response:", caseResponse);
      console.log("✅ Extractions response:", extractionsResponse);

      // Process case info
      const caseInfo = caseResponse.DermCase?.[0]?.properties;
      if (caseInfo) {
        setCaseData({
          caseId: caseInfo.caseId,
          fileName: caseInfo.fileName,
          filePath: caseInfo.filePath,
          patientId: caseInfo.patientId,
          createdAt: caseInfo.createdAt,
          mimeType: caseInfo.mimeType,
        });
      }

      // Process LLM interactions
      const llmData = caseResponse.LLMInteraction || [];
      setLlmInteractions(llmData);
      console.log(`✅ Loaded ${llmData.length} LLM interactions`);

      // Process segmentation results
      const segData = caseResponse.SegmentationResult || [];
      setSegmentationResults(segData);
      console.log(`✅ Loaded ${segData.length} segmentation results`);

      // Process STORED extractions (no recreation)
      if (extractionsResponse.entities_found) {
        setExtractedEntities(extractionsResponse.entities);
        console.log(`✅ Loaded ${extractionsResponse.entities.length} stored entities`);
      } else {
        console.log("ℹ️ No stored entities found");
        setExtractedEntities([]);
      }

      if (extractionsResponse.structured_found && extractionsResponse.structured) {
        setStructuredExtraction(extractionsResponse.structured);
        console.log("✅ Loaded stored structured extraction");
      } else {
        console.log("ℹ️ No stored structured extraction found");
        setStructuredExtraction(null);
      }

    } catch (err: any) {
      console.error("❌ Error fetching case data:", err);
      setError(err.message || "Failed to load case data");
    } finally {
      setLoading(false);
    }
  };

  const handleAccordionChange = (panel: string) => {
    setExpandedAccordions((prev) => ({
      ...prev,
      [panel]: !prev[panel],
    }));
  };

  const handleActivateVersion = async (versionId: string) => {
    try {
      const accessToken = await authService.getAccessToken();
      if (!accessToken) throw new Error("No access token available");
      
      // Find the version being activated to check if it's v0
      const targetVersion = versions.find(v => v.id === versionId);
      const isVersion0 = targetVersion?.versionNumber === 0;
      
      await activateVersion(accessToken, versionId);
      
      // For version 0, add a small delay and retry to ensure backend updates propagate
      if (isVersion0) {
        console.log("🔄 Activating version 0 - adding retry mechanism");
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Refresh versions and get the newly active version
      let newActiveVersion = await fetchVersions();
      
      // If version 0 activation didn't work, retry once more
      if (isVersion0 && newActiveVersion?.versionNumber !== 0) {
        console.log("🔄 Version 0 activation needs retry...");
        await new Promise(resolve => setTimeout(resolve, 500));
        await activateVersion(accessToken, versionId);
        newActiveVersion = await fetchVersions();
      }
      
      // Fetch extractions for the newly activated version
      if (newActiveVersion) {
        await fetchCaseData(newActiveVersion.versionNumber);
      } else {
        await fetchCaseData();
      }
      
      console.log("✅ Version activated and data refreshed");
    } catch (err: any) {
      console.error("❌ Error activating version:", err);
      setError("Failed to activate version");
    }
  };

  const handleTriggerExtraction = async () => {
    if (!caseId) return;
    
    setExtracting(true);
    try {
      const accessToken = await authService.getAccessToken();
      if (!accessToken) throw new Error("No access token available");
      
      console.log("🔄 Triggering extraction pipeline for case:", caseId);
      
      // Run extractions in sequence
      await extractEntities(accessToken, caseId);
      console.log("✅ Entity extraction completed");
      
      await extractStructured(accessToken, caseId);
      console.log("✅ Structured extraction completed");
      
      // Refresh versions first, then case data with active version
      const currentActiveVersion = await fetchVersions();
      if (currentActiveVersion) {
        await fetchCaseData(currentActiveVersion.versionNumber);
      } else {
        await fetchCaseData();
      }
      
      console.log("✅ Extraction complete and data refreshed");
      
    } catch (err: any) {
      console.error("❌ Error during extraction:", err);
      setError("Failed to extract data");
    } finally {
      setExtracting(false);
    }
  };

  const handleViewReport = async (reportId: string) => {
    try {
      const accessToken = await authService.getAccessToken();
      if (!accessToken) throw new Error("No access token available");
      
      const report = await getReportById(accessToken, reportId);
      setSelectedReportContent(report.markdownContent);
      
      // Scroll to report view
      setTimeout(() => {
        document.getElementById('report-content')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (err: any) {
      console.error("❌ Error fetching report:", err);
      setError("Failed to load report");
    }
  };

  const handleCreateNewReport = () => {
    // Check if extraction is pending
    if (activeVersion && activeVersion.extractionStatus === "pending") {
      setShowExtractionAlert(true);
      // Auto-hide alert after 5 seconds
      setTimeout(() => setShowExtractionAlert(false), 5000);
      return;
    }
    
    // Proceed with navigation if extraction is complete
    navigate(`/cases/${caseId}?patientId=${patientId || ""}&baseVersion=${activeVersion?.versionNumber}`);
  };

  const getMalignancyColor = (risk: string) => {
    switch (risk?.toLowerCase()) {
      case "malignant":
        return theme.palette.error.main;
      case "premalignant":
        return theme.palette.warning.main;
      case "benign":
        return theme.palette.success.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage?.toLowerCase()) {
      case "uploadfile":
        return theme.palette.primary.main;
      case "xai":
        return theme.palette.info.main;
      case "followup":
        return theme.palette.secondary.main;
      case "segmentation":
        return theme.palette.warning.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Loading case details...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
       
        <Card>
        
          <CardContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6">Error loading case</Typography>
              <Typography>{error}</Typography>
            </Alert>
            <Stack direction="row" spacing={2}>
              <Button 
                variant="contained" 
                onClick={() => fetchCaseData()}
                startIcon={<RefreshIcon />}
              >
                Retry
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate("/images")}
                startIcon={<ArrowBackIcon />}
              >
                Back to Cases
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Back Navigation */}
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          variant="text"
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            }
          }}
        >
          Back
        </Button>
      </Box>
        
      <Card
        elevation={3}
        sx={{
          borderRadius: { xs: 2, sm: 3 },
          background: isDarkMode
            ? "linear-gradient(to bottom, rgba(35,35,40,0.9), rgba(25,25,30,1))"
            : "linear-gradient(to bottom, rgba(255,255,255,0.9), rgba(250,250,255,1))",
          boxShadow: isDarkMode
            ? "0 8px 32px rgba(0,0,0,0.3)"
            : "0 8px 32px rgba(100,100,255,0.1)",
        }}
      >
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" spacing={2}>
              <Tooltip title="Back to Cases">
                <IconButton onClick={() => navigate("/images")} size="small">
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
              <InfoIcon color="primary" />
              <Typography variant="h5" component="div">
                Case Review
              </Typography>
            </Stack>
          }
          subheader={caseData?.fileName || "Case Details"}
          action={
            <Stack direction="row" spacing={1}>
              <Button
                onClick={handleStartTour}
                startIcon={isSmallScreen ? undefined : <SchoolIcon />}
                variant="contained"
                color="secondary"
                size="small"
                sx={{
                  backgroundColor: "#e91e63",
                  color: "#ffffff",
                  textTransform: "none",
                  minWidth: isSmallScreen ? "40px" : "auto",
                  px: isSmallScreen ? 1 : 2,
                  "&:hover": {
                    backgroundColor: "#d81b60",
                  },
                }}
              >
                {isSmallScreen ? <SchoolIcon /> : "Guide"}
              </Button>
              <Tooltip title="Refresh Data">
                <IconButton onClick={() => fetchCaseData()} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit Report">
                <IconButton
                  onClick={() => navigate(`/cases/${caseId}?patientId=${patientId || ""}`)}
                  color="primary"
                >
                  {/* <EditIcon /> */}
                </IconButton>
              </Tooltip>
            </Stack>
          }
        />

        <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
           {/* Add Finalized Reports Links before the main card */}
       {caseId && (
        <FinalizedReportsLinks caseId={caseId} onReportClick={handleViewReport} />
      )}

          {/* Version Control Section */}
          <Paper
            data-tour="version-control"
            elevation={2}
            sx={{
              mb: 3,
              p: 2.5,
              borderRadius: 2,
              background: isDarkMode
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.2)} 0%, ${alpha(theme.palette.secondary.dark, 0.1)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.15)} 0%, ${alpha(theme.palette.secondary.light, 0.1)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            }}
          >
            {loadingVersions ? (
              <Stack direction="row" alignItems="center" spacing={2}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Loading version information...
                </Typography>
              </Stack>
            ) : activeVersion ? (
              <Stack spacing={2}>
                {/* Extraction Alert */}
                {showExtractionAlert && (
                  <Alert 
                    severity="warning" 
                    onClose={() => setShowExtractionAlert(false)}
                    sx={{ 
                      borderRadius: 2,
                      '& .MuiAlert-message': {
                        width: '100%'
                      }
                    }}
                  >
                    Please complete the knowledge graph extraction before creating a new report version. Click 'Generate Knowledge Graph' above to start extraction.
                  </Alert>
                )}
                
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <HistoryIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      Version Control
                    </Typography>
                    {activeVersion.versionNumber === 0 ? (
                      <Chip
                        data-tour="active-version"
                        label="Initial Analysis"
                        icon={<ScienceIcon fontSize="small" />}
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    ) : (
                      <Chip
                        data-tour="active-version"
                        label={`Version ${activeVersion.versionNumber} Active`}
                        icon={<CheckCircleIcon fontSize="small" />}
                        color="success"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {activeVersion.extractionStatus === "pending" && activeVersion.reportId && (
                      <Chip
                        label={isSmallScreen ? "" : "Extraction Pending"}
                        icon={<HourglassEmptyIcon fontSize="small" />}
                        color="warning"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>

                  {/* Action Buttons */}
                  <Stack direction="row" spacing={1}>
                    {/* Extraction Trigger Button - Always visible for finalized reports */}
                    {activeVersion.reportId && (
                      <Button
                        data-tour="generate-kg"
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleTriggerExtraction}
                        disabled={extracting}
                        startIcon={extracting ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                        }}
                      >
                        {extracting ? "Extracting..." : activeVersion.extractionStatus === "extracted" ? "Re-generate Knowledge Graph" : "Generate Knowledge Graph"}
                      </Button>
                    )}
                    
                    {/* Create New Report Button */}
                    <Button
                      data-tour="new-report"
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={handleCreateNewReport}
                      startIcon={<AddIcon />}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                      }}
                    >
                      New Report from v{activeVersion.versionNumber}
                    </Button>
                  </Stack>
                </Stack>

                {/* Version List */}
                {versions.length > 0 && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Available Versions:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                      {versions.map((version) => (
                        <Tooltip
                          key={version.id}
                          title={
                            version.isActive
                              ? "Currently Active"
                              : `Click to activate version ${version.versionNumber}`
                          }
                          arrow
                        >
                          <Chip
                            label={
                              version.versionNumber === 0
                                ? "Initial (v0)"
                                : `Report v${version.versionNumber}`
                            }
                            onClick={() => !version.isActive && handleActivateVersion(version.id)}
                            color={version.isActive ? "success" : "default"}
                            variant={version.isActive ? "filled" : "outlined"}
                            size="small"
                            icon={
                              version.versionNumber === 0 ? (
                                <ScienceIcon fontSize="small" />
                              ) : (
                                <DescriptionIcon fontSize="small" />
                              )
                            }
                            sx={{
                              fontWeight: version.isActive ? 600 : 400,
                              cursor: version.isActive ? "default" : "pointer",
                              "&:hover": !version.isActive
                                ? {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    borderColor: theme.palette.primary.main,
                                  }
                                : {},
                            }}
                          />
                        </Tooltip>
                      ))}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                      {activeVersion.versionNumber === 0
                        ? "Showing initial AI analysis. Switch to a report version to see refined taxonomies."
                        : `Taxonomies and filters use data from version ${activeVersion.versionNumber}.`}
                    </Typography>
                  </Box>
                )}
              </Stack>
            ) : (
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <HistoryIcon color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Version Control
                  </Typography>
                  <Chip
                    label="No Versions"
                    icon={<InfoIcon fontSize="small" />}
                    color="default"
                    size="small"
                    variant="outlined"
                  />
                </Stack>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    No report versions exist for this case yet.
                  </Typography>
                  <Typography variant="body2">
                    Create a finalized report to enable version control and knowledge graph extraction.
                  </Typography>
                  <Button
                    data-tour="create-report"
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => navigate(`/cases/${caseId}?patientId=${patientId || ""}`)}
                    startIcon={<EditIcon />}
                    sx={{ mt: 1.5, textTransform: 'none' }}
                  >
                    Create Report
                  </Button>
                </Alert>
              </Stack>
            )}
          </Paper>

          {/* Selected Report Viewer */}
          {selectedReportContent && (
            <Paper
              id="report-content"
              elevation={3}
              sx={{
                mb: 3,
                p: 3,
                borderRadius: 2,
                border: `2px solid ${theme.palette.success.main}`,
                backgroundColor: alpha(theme.palette.success.main, 0.02),
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <DescriptionIcon color="success" />
                  <Typography variant="h6" fontWeight={600}>
                    Report Preview
                  </Typography>
                </Stack>
                <IconButton
                  size="small"
                  onClick={() => setSelectedReportContent(null)}
                  sx={{ color: theme.palette.text.secondary }}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  backgroundColor: alpha(theme.palette.background.paper, 0.5),
                  maxHeight: "500px",
                  overflowY: "auto",
                }}
              >
                <ReactMarkdown>{removeAttachedImagesSection(selectedReportContent) || ''}</ReactMarkdown>
              </Box>
            </Paper>
          )}

          {/* Case Metadata Chips */}
          <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: "wrap", gap: 1 }}>
            <Chip 
              label={`Case ID: ${caseId}`} 
              size="small" 
              color="primary" 
              variant="outlined"
              icon={<InfoIcon fontSize="small" />}
            />
            {patientId && (
              <Chip 
                label={`Patient ID: ${patientId}`} 
                size="small" 
                color="secondary" 
                variant="outlined" 
              />
            )}
            {caseData?.createdAt && (
              <Chip
                label={`Created: ${new Date(caseData.createdAt).toLocaleDateString()}`}
                size="small"
                variant="outlined"
              />
            )}
            {caseData?.mimeType && (
              <Chip
                label={caseData.mimeType}
                size="small"
                variant="outlined"
              />
            )}
          </Stack>

          {/* Case Image */}
          {caseData?.filePath && (
            <Fade in={true} timeout={800}>
              <Paper
                elevation={2}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  overflow: "hidden",
                  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                  backgroundColor: alpha(theme.palette.background.paper, 0.5),
                }}
              >
                <Box sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Original Image
                  </Typography>
                  <img
                    src={baseUrl + caseData.filePath.replace(/^\/+/, "")}
                    alt={caseData.fileName}
                    style={{
                      width: "100%",
                      maxHeight: "500px",
                      objectFit: "contain",
                      borderRadius: "8px",
                    }}
                  />
                </Box>
              </Paper>
            </Fade>
          )}

          <Divider sx={{ my: 3 }} />

          {/* AI Analysis / Report Content Accordion */}
          {(activeReportContent || llmInteractions.length > 0) && (
            <Accordion
              data-tour="report-content"
              expanded={expandedAccordions.llm}
              onChange={() => handleAccordionChange("llm")}
              sx={{
                mb: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                borderRadius: 2,
                "&:before": { display: "none" },
                backgroundColor: alpha(theme.palette.primary.main, 0.02),
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                  {activeReportContent ? <DescriptionIcon color="primary" /> : <BiotechIcon color="primary" />}
                  <Typography variant="h6">
                    {activeReportContent 
                      ? `Finalized Report (v${activeVersion?.versionNumber})` 
                      : "AI Analysis"}
                  </Typography>
                  {!activeReportContent && (
                    <Chip 
                      label={`${llmInteractions.length} interactions`} 
                      size="small" 
                      color="primary"
                    />
                  )}
                  {activeReportContent && activeVersion && (
                    <Chip 
                      label="Active Version" 
                      size="small" 
                      color="success"
                    />
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {activeReportContent ? (
                  // Show report content for report versions
                  <Paper
                    elevation={1}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.background.paper, 0.6),
                    }}
                  >
                    <ReactMarkdown>{removeAttachedImagesSection(activeReportContent) || ''}</ReactMarkdown>
                  </Paper>
                ) : (
                  // Show LLM interactions for v0 (initial analysis)
                  <Stack spacing={2}>
                  {llmInteractions.map((interaction, idx) => (
                    <Paper
                      key={interaction.id}
                      elevation={1}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        borderLeft: `4px solid ${getStageColor(interaction.data.stage)}`,
                        backgroundColor: alpha(theme.palette.background.paper, 0.6),
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                        <Chip
                          label={interaction.data.stage?.toUpperCase() || `Analysis ${idx + 1}`}
                          size="small"
                          sx={{
                            backgroundColor: getStageColor(interaction.data.stage),
                            color: "#fff",
                          }}
                        />
                        {interaction.data.confidence && (
                          <Chip
                            label={`${Math.round(interaction.data.confidence * 100)}% confidence`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {/* {interaction.data.model && (
                          <Chip
                            label={interaction.data.model}
                            size="small"
                            variant="outlined"
                          />
                        )} */}
                      </Stack>
                      
                      <Box sx={{ 
                        p: 2, 
                        borderRadius: 1,
                        backgroundColor: alpha(theme.palette.background.default, 0.5),
                      }}>
                        <ReactMarkdown>{interaction.data.response}</ReactMarkdown>
                      </Box>
                      
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ mt: 1.5, display: "block" }}
                      >
                        {new Date(interaction.data.createdAt).toLocaleString()}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          {/* Segmentation Results Accordion */}
          {segmentationResults.length > 0 && (
            <Accordion
              data-tour="segmentation"
              expanded={expandedAccordions.segmentation}
              onChange={() => handleAccordionChange("segmentation")}
              sx={{
                mb: 2,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                borderRadius: 2,
                "&:before": { display: "none" },
                backgroundColor: alpha(theme.palette.secondary.main, 0.02),
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <AssessmentIcon color="secondary" />
                  <Typography variant="h6">Segmentation Analysis</Typography>
                  <Chip 
                    label={`${segmentationResults.length} result${segmentationResults.length > 1 ? 's' : ''}`}
                    size="small"
                    color="secondary"
                  />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={3}>
                  {segmentationResults.map((result) => (
                    <Box key={result.id}>
                      {/* Metrics */}
                      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
                        <Chip
                          label={`Circularity: ${result.data.circularity?.toFixed(3) || 'N/A'}`}
                          size="small"
                          color="info"
                        />
                        <Chip
                          label={`Asymmetry: ${result.data.asymmetry?.toFixed(3) || 'N/A'}`}
                          size="small"
                          color="warning"
                        />
                      </Stack>

                      {/* Profile Text */}
                      {result.data.profileText && result.data.profileText !== "unknown" && (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            mb: 2, 
                            whiteSpace: "pre-line",
                            p: 2,
                            borderRadius: 1,
                            backgroundColor: alpha(theme.palette.background.default, 0.5),
                          }}
                        >
                          {result.data.profileText}
                        </Typography>
                      )}

                      {/* Image Grid */}
                      <Box 
                        sx={{ 
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                          gap: 2,
                        }}
                      >
                        {result.data.segmentedPlot && (
                          <Paper elevation={2} sx={{ p: 1.5, borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                              Segmented Image
                            </Typography>
                            <img
                              src={baseUrl + result.data.segmentedPlot.replace(/^\/+/, "")}
                              alt="Segmented"
                              style={{ width: "100%", borderRadius: 8 }}
                            />
                          </Paper>
                        )}
                        {result.data.assymetryPlot1 && (
                          <Paper elevation={2} sx={{ p: 1.5, borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                              Asymmetry Analysis 1
                            </Typography>
                            <img
                              src={baseUrl + result.data.assymetryPlot1.replace(/^\/+/, "")}
                              alt="Asymmetry 1"
                              style={{ width: "100%", borderRadius: 8 }}
                            />
                          </Paper>
                        )}
                        {result.data.assymetryplot2 && (
                          <Paper elevation={2} sx={{ p: 1.5, borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                              Asymmetry Analysis 2
                            </Typography>
                            <img
                              src={baseUrl + result.data.assymetryplot2.replace(/^\/+/, "")}
                              alt="Asymmetry 2"
                              style={{ width: "100%", borderRadius: 8 }}
                            />
                          </Paper>
                        )}
                        {result.data.colorplot && (
                          <Paper elevation={2} sx={{ p: 1.5, borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                              Color Analysis
                            </Typography>
                            <img
                              src={baseUrl + result.data.colorplot.replace(/^\/+/, "")}
                              alt="Color Plot"
                              style={{ width: "100%", borderRadius: 8 }}
                            />
                          </Paper>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Extracted Entities Accordion */}
          <Accordion
            data-tour="entities"
            expanded={expandedAccordions.entities}
            onChange={() => handleAccordionChange("entities")}
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
              backgroundColor: alpha(theme.palette.info.main, 0.02),
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CategoryIcon color="info" />
                <Typography variant="h6">Extracted Medical Entities (NER)</Typography>
                <Chip 
                  label={`${extractedEntities.length} entities`} 
                  size="small" 
                  color="info" 
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {loadingEntities ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : extractedEntities.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {extractedEntities.map((entity, idx) => (
                    <Chip
                      key={idx}
                      label={`${entity.entity}${entity.label ? ` (${entity.label})` : ''}`}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{
                        fontWeight: 500,
                      }}
                    />
                  ))}
                </Stack>
              ) : (
                <Alert severity="info">
                  No entities extracted yet. Entity extraction runs automatically after case analysis.
                </Alert>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Structured Clinical Assessment Accordion */}
          <Accordion
            data-tour="structured"
            expanded={expandedAccordions.structured}
            onChange={() => handleAccordionChange("structured")}
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
              borderRadius: 2,
              "&:before": { display: "none" },
              backgroundColor: alpha(theme.palette.success.main, 0.02),
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AssessmentIcon color="success" />
                <Typography variant="h6">Structured Clinical Assessment</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {loadingStructured ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : structuredExtraction ? (
                <Stack spacing={3}>
                  {/* Finding Description */}
                  <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Finding Description
                    </Typography>
                    <Typography variant="body2">
                      {structuredExtraction.finding_description}
                    </Typography>
                  </Paper>

                  {/* Primary Diagnosis */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Primary Diagnosis
                    </Typography>
                    <Chip 
                      label={structuredExtraction.primary_diagnosis} 
                      color="success"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>

                  {/* Differential Diagnoses */}
                  {structuredExtraction.differential_diagnoses?.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Differential Diagnoses
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                        {structuredExtraction.differential_diagnoses.map((diag, idx) => (
                          <Chip 
                            key={idx} 
                            label={diag} 
                            size="small" 
                            variant="outlined"
                            color="warning"
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* Location & Malignancy in a row */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Location
                      </Typography>
                      <Chip 
                        label={structuredExtraction.finding_location}
                        size="small"
                        variant="outlined"
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Malignancy Risk
                      </Typography>
                      <Chip
                        label={structuredExtraction.malignancy_risk?.toUpperCase() || 'UNKNOWN'}
                        size="small"
                        sx={{
                          backgroundColor: getMalignancyColor(structuredExtraction.malignancy_risk),
                          color: "#fff",
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </Stack>

                  {/* Characteristics */}
                  {structuredExtraction.characteristics && (
                    <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Characteristics
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                        {Object.entries(structuredExtraction.characteristics).map(([key, value]) => (
                          value && value !== "unknown" && (
                            <Chip
                              key={key}
                              label={`${key}: ${value}`}
                              size="small"
                              variant="outlined"
                              color="secondary"
                            />
                          )
                        ))}
                      </Stack>
                    </Paper>
                  )}

                  {/* Risk Factors */}
                  {structuredExtraction.risk_factors?.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Risk Factors
                      </Typography>
                      <Stack spacing={1}>
                        {structuredExtraction.risk_factors.map((factor, idx) => (
                          <Typography key={idx} variant="body2">
                            • {factor}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* Recommendations */}
                  {structuredExtraction.recommendations?.length > 0 && (
                    <Paper 
                      elevation={1} 
                      sx={{ 
                        p: 2, 
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.info.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      }}
                    >
                      <Typography variant="subtitle2" color="info.main" gutterBottom fontWeight={600}>
                        Clinical Recommendations
                      </Typography>
                      <Stack spacing={1}>
                        {structuredExtraction.recommendations.map((rec, idx) => (
                          <Typography key={idx} variant="body2" sx={{ pl: 1 }}>
                            {idx + 1}. {rec}
                          </Typography>
                        ))}
                      </Stack>
                    </Paper>
                  )}
                </Stack>
              ) : (
                <Alert severity="info">
                  No structured extraction available yet. Structured extraction runs automatically after entity extraction.
                </Alert>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Knowledge Graph Accordion */}
          {structuredExtraction && (
            <Accordion
              data-tour="knowledge-graph"
              expanded={expandedAccordions.knowledgeGraph}
              onChange={() => handleAccordionChange("knowledgeGraph")}
              sx={{
                mb: 2,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                borderRadius: 2,
                "&:before": { display: "none" },
                backgroundColor: alpha(theme.palette.warning.main, 0.02),
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <AccountTreeIcon color="warning" />
                  <Typography variant="h6">Knowledge Graph</Typography>
                  <Chip label="BETA" size="small" color="warning" />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ minHeight: 500 }}>
                  <KnowledgeGraph structuredData={structuredExtraction} />
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Empty State */}
          {!llmInteractions.length && !segmentationResults.length && !loadingEntities && !loadingStructured && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                No analysis data available for this case yet.
              </Typography>
              <Typography variant="body2">
                Use the "Edit Report" button to create an analysis for this case.
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      <TourGuide
        steps={tourSteps}
        tourId="case-details"
        run={runTour}
        continuous={true}
        showSkipButton={true}
        showProgress={true}
        onFinish={handleFinishTour}
        onSkip={handleFinishTour}
      />
    </Box>
  );
}